import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/** Bearer-token fallback for mobile browsers that drop cross-site
 *  cookies (iOS Safari ITP, Android Chrome incognito, Firefox Focus,
 *  in-app webviews, etc). The backend's `get_current_user` already
 *  accepts either an `access_token` cookie OR
 *  `Authorization: Bearer <token>`, so we can ship whichever the
 *  environment supports. `/auth/login` and `/auth/register` now
 *  include `access_token` in the response body — we stash it in
 *  localStorage and add it to every subsequent request. */
const TOKEN_KEY = "bsvac_access_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function setStoredToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode / storage disabled — cookies still work */ }
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // Longer default for slow LTE/mobile connections. Individual calls
  // (uploads) override as needed.
  timeout: 30000,
});

// Attach Bearer token if we have one.
api.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
  }
  return config;
});

const REFRESH_KEY = "bsvac_refresh_token";
export function setStoredRefresh(t) {
  try { if (t) localStorage.setItem(REFRESH_KEY, t); else localStorage.removeItem(REFRESH_KEY); } catch { /* storage disabled */ }
}
const getStoredRefresh = () => { try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; } };

// Single-flight refresh: concurrent 401s share one /auth/refresh call.
let refreshing = null;
function refreshSession() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE}/auth/refresh`, { refresh_token: getStoredRefresh() }, { withCredentials: true, timeout: 30000 })
      .then(({ data }) => { setStoredToken(data.access_token); setStoredRefresh(data.refresh_token); return data.access_token; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

// Expired/invalid access token -> refresh once and retry the request.
// If the refresh fails too, clear tokens and tell the app the session ended.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const detail = String(err?.response?.data?.detail || "");
    const cfg = err?.config || {};
    const isAuthCall = /\/auth\/(login|refresh|register|logout)/.test(cfg.url || "");
    if (status === 401 && /token/i.test(detail) && !isAuthCall && !cfg._retried) {
      try {
        const fresh = await refreshSession();
        cfg._retried = true;
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
        return api(cfg);
      } catch {
        setStoredToken(null); setStoredRefresh(null);
        window.dispatchEvent(new Event("bsvac:session-expired"));
      }
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
